import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from "react-native";
import { MapPin, type LucideIcon } from "lucide-react-native";
import {
  getPlaceDetails,
  searchPlaces,
  type PlacePrediction,
} from "@src/packages/clinics/utils/googlePlaces";
import { useDebounce } from "@src/packages/hooks/useDebounce";
import { colors, typography } from "../theme/tokens";
import { MoonFieldInput } from "./MoonFieldInput";
import { MoonFormField } from "./MoonFormField";
import { MoonText } from "./MoonText";
import { useInputFontSize } from "../utils/inputFontSize";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Inline address field with Google Places autocomplete dropdown.
 *
 * Mirrors the `MoonPhoneField` pattern: a single TextInput with a
 * suggestion list that drops down below as the user types. No modal,
 * no second tap to save — pick a prediction and the parent receives a
 * structured `AddressData` payload built from the resolved place details.
 *
 * The on-screen text reflects the most recent free-text query while
 * typing, then snaps to the prediction's full description once selected.
 * Free text is reported as `address1` as the user types, so callers can
 * save it without requiring a prediction or a blur event. The other
 * structured fields remain null in that fallback.
 */
export type AddressData = {
  address1: string | null;
  address2: string | null;
  city: string | null;
  /** State/region short code, e.g. the US 2-letter "NY". */
  state: string | null;
  postalCode: string | null;
  country: string | null;
};

type Props = {
  label: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  /** Short supporting copy rendered directly beneath the field label. */
  labelHint?: string;
  /**
   * Optional icon inside the input, before the address text. Defaults to a
   * map pin; pass `null` when the label icon already provides that context.
   */
  icon?: ReactNode;
  /** Display string of the current address (e.g. "123 Main St, NYC, USA"). */
  value: string | null;
  onChange: (data: AddressData) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
};

function extractAddressFields(details: {
  formattedAddress: string;
  addressComponents: { longName: string; shortName: string; types: string[] }[];
}): AddressData {
  let streetNumber = "";
  let route = "";
  let city: string | null = null;
  let state: string | null = null;
  let postalCode: string | null = null;
  let country: string | null = null;

  for (const component of details.addressComponents) {
    if (component.types.includes("street_number")) {
      streetNumber = component.longName;
    } else if (component.types.includes("route")) {
      route = component.longName;
    } else if (component.types.includes("locality")) {
      city = component.longName;
    } else if (component.types.includes("administrative_area_level_1")) {
      // Prefer the short code (e.g. "NY") for the state field; still use the
      // long name as a city fallback when no locality component was returned.
      state = component.shortName;
      if (!city) city = component.longName;
    } else if (component.types.includes("postal_code")) {
      postalCode = component.longName;
    } else if (component.types.includes("country")) {
      // The code, not the name. Google gives both, and everything that reads
      // this expects ISO-3166 alpha-2: normalizeCountryCode rejects anything
      // that is not two letters, so "France" was stored and then silently
      // discarded, leaving the patient with no market at all.
      country = component.shortName || component.longName;
    }
  }

  const street = [streetNumber, route].filter(Boolean).join(" ").trim();
  return {
    address1: street || details.formattedAddress || null,
    address2: null,
    city,
    state,
    postalCode,
    country,
  };
}

export function AddressField({
  label,
  labelIcon,
  labelHint,
  icon,
  value,
  onChange,
  placeholder = "Start typing your address",
  error,
  hint,
  required,
  disabled,
  loading,
  testID,
}: Props) {
  const fontSize = useInputFontSize();
  const capturePress = usePressCapture("AddressField");
  const [query, setQuery] = useState(value ?? "");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const inputRef = useRef<TextInput | null>(null);
  const selectedQueryRef = useRef<string | null>(null);
  // A programmatic blur can fire before React has applied the selected
  // prediction to `query`. Keep the free-text fallback off until the
  // structured address selection has settled; otherwise that blur can
  // overwrite the structured AddressData with a single-line address.
  const selectionInProgressRef = useRef(false);
  // Suppress the next debounced search effect after handleSelect — the
  // search would otherwise re-fire with the prediction's full description
  // and pop the dropdown back open. Cleared by the next user keystroke.
  const suppressSearchRef = useRef(false);
  const debouncedQuery = useDebounce(query, 300);

  // No useEffect re-syncs query from `value`. The input is initialised
  // from value on mount and is user-controlled afterwards. Re-syncing
  // would clobber the displayed text whenever onChange triggers a
  // patient refetch (the parent rebuilds `value` as a joined string
  // like "123 Main, NYC, USA, USA", which differs from the prediction's
  // formatted description) — users would see their selection visibly
  // replaced after picking it, even though the address persisted fine.

  useEffect(() => {
    if (suppressSearchRef.current) {
      suppressSearchRef.current = false;
      return;
    }
    const trimmed = debouncedQuery.trim();
    if (trimmed.length < 3 || trimmed === (value ?? "").trim()) {
      setPredictions([]);
      return;
    }
    let cancelled = false;
    searchPlaces(trimmed)
      .then((results) => {
        if (cancelled) return;
        setPredictions(results);
        if (results.length > 0) setOpen(true);
      })
      .catch((err) => {
        console.error("[AddressField] searchPlaces failed:", err);
        if (!cancelled) setPredictions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, value]);

  const handleSelect = async (prediction: PlacePrediction) => {
    selectionInProgressRef.current = true;
    suppressSearchRef.current = true;
    selectedQueryRef.current = prediction.description;
    setOpen(false);
    setPredictions([]);
    setQuery(prediction.description);
    inputRef.current?.blur();
    setResolving(true);
    try {
      const details = await getPlaceDetails(prediction.placeId);
      if (details) {
        onChange(extractAddressFields(details));
      } else {
        onChange({
          address1: prediction.description,
          address2: null,
          city: null,
          state: null,
          postalCode: null,
          country: null,
        });
      }
    } catch (err) {
      console.error("[AddressField] getPlaceDetails failed:", err);
      onChange({
        address1: prediction.description,
        address2: null,
        city: null,
        state: null,
        postalCode: null,
        country: null,
      });
    } finally {
      selectionInProgressRef.current = false;
      setResolving(false);
    }
  };

  const handleBlur = () => {
    // Slight delay so a tap on a prediction row fires its onPress before
    // the dropdown closes.
    setTimeout(() => setOpen(false), 150);
    if (selectionInProgressRef.current) return;

    const nextAddress1 = query.trim();
    const savedAddress = (value ?? "").trim();

    // A prediction selection is always persisted with structured details. A
    // delayed native blur must not replace it with the prediction's display
    // string as an unstructured address.
    if (nextAddress1 === selectedQueryRef.current) return;

    // A user does not need to select an autocomplete result. Persist their
    // entered address as address1 and leave the optional structured parts
    // empty. A selected prediction still takes the structured path above.
    if (!nextAddress1 && savedAddress) {
      onChange({
        address1: null,
        address2: null,
        city: null,
        state: null,
        postalCode: null,
        country: null,
      });
    } else if (nextAddress1 && nextAddress1 !== savedAddress) {
      onChange({
        address1: nextAddress1,
        address2: null,
        city: null,
        state: null,
        postalCode: null,
        country: null,
      });
    }
  };

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      labelHint={labelHint}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      <View>
        <MoonFieldInput hasError={!!error} paddingY={2}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              paddingVertical: 10,
            }}
          >
            {icon === undefined ? (
              <MapPin size={13} color={colors.textLight} strokeWidth={1.5} />
            ) : (
              icon
            )}
            <TextInput
              ref={inputRef}
              value={query}
              onChangeText={(text) => {
                // Re-arm the search after a selection: typing means
                // the user wants fresh predictions, not the silenced
                // re-search of the prediction's full description.
                suppressSearchRef.current = false;
                selectedQueryRef.current = null;
                setQuery(text);
                onChange({
                  address1: text.trim() || null,
                  address2: null,
                  city: null,
                  state: null,
                  postalCode: null,
                  country: null,
                });
                if (text.trim().length >= 3) setOpen(true);
              }}
              onFocus={() => {
                if (predictions.length > 0) setOpen(true);
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              placeholderTextColor={colors.textFaint}
              editable={!disabled}
              autoCorrect={false}
              spellCheck={false}
              // No `autoComplete` / `textContentType` — those trigger
              // the browser's native autofill on web, which renders its
              // own dropdown on top of ours and races onChangeText with
              // the autofilled value.
              autoComplete="off"
              style={{
                flex: 1,
                marginLeft: icon === null ? 0 : 10,
                fontSize,
                color: colors.text,
                fontFamily: "DMSans_400Regular",
                ...(Platform.OS === "web" ? { outlineStyle: "none" } : null),
              }}
            />
            {resolving ? (
              <ActivityIndicator size="small" color={colors.warm} />
            ) : null}
          </View>
        </MoonFieldInput>

        {open && predictions.length > 0 && !disabled ? (
          <View
            style={{
              marginTop: 6,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.12)",
              backgroundColor: "#FFFFFF",
              overflow: "hidden",
              maxHeight: 280,
              ...Platform.select({
                ios: {
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.08,
                  shadowRadius: 12,
                },
                android: { elevation: 6 },
                web: { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
              }),
            }}
          >
            <ScrollView
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
            >
              {predictions.map((prediction) => (
                <Pressable
                  key={prediction.placeId}
                  onPress={() => {
                    capturePress(
                      pressLabel(prediction.description),
                      "address_suggestion",
                    );
                    void handleSelect(prediction);
                  }}
                >
                  {({ pressed }) => (
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingHorizontal: 12,
                        paddingVertical: 10,
                        backgroundColor: pressed
                          ? "rgba(0,0,0,0.04)"
                          : "transparent",
                      }}
                    >
                      <MapPin
                        size={13}
                        color={colors.textFaint}
                        strokeWidth={1.5}
                      />
                      <View style={{ flex: 1, marginLeft: 10 }}>
                        <MoonText
                          style={{
                            fontFamily: typography.families.sansMedium,
                            fontSize: 13,
                            color: colors.text,
                            fontWeight: "500",
                          }}
                          numberOfLines={1}
                        >
                          {prediction.structuredFormatting.mainText}
                        </MoonText>
                        <MoonText
                          style={{
                            fontFamily: typography.families.sans,
                            fontSize: 12,
                            color: colors.textFaint,
                            marginTop: 2,
                          }}
                          numberOfLines={1}
                        >
                          {prediction.structuredFormatting.secondaryText}
                        </MoonText>
                      </View>
                    </View>
                  )}
                </Pressable>
              ))}
            </ScrollView>
          </View>
        ) : null}
      </View>
    </MoonFormField>
  );
}
